
import { Options, prop, Vue } from 'vue-class-component';
import StatusHandler from '@/app/ui/components/status-handler/index.vue';
import LpButton from '@/app/ui/components/lp-button/index.vue';
import Checkbox from 'primevue/checkbox';
import RadioButton from "primevue/radiobutton";
import { SearchFlight } from '@/domain/entities/Cargo';
import ScheduleCardV2 from './schedule-card-v2.vue';
import { CargoController } from '@/app/ui/controllers/CargoController';
import { FlagsPermissionHydra } from "@/feature-flags/flags-hydra";
import CargoTableSttSection from "@/app/ui/views/cargo/booking-cargo/third-step/cargo-table-stt-section.vue";

class Props {
  isLoading = prop<boolean>({
    type: Boolean,
    default: false
  });
  error = prop<string>({
    type: String,
    default: ""
  });
  data = prop<SearchFlight[]>({
    default: () => [],
    type: Object
  });
}

@Options({
  emits: ["onChangeSearch", "onReload"],
  components: {
    StatusHandler,
    Checkbox,
    RadioButton,
    ScheduleCardV2,
    CargoTableSttSection
  }
})

export default class LayoutSchedule extends Vue.with(Props) {
  openFilter = false;
  isSortFilter = true;
  localTransitNumber: any[] = [];
  transitNumber: any[] = [];
  flightTime = "";
  airportFilter = false;
  localListAirportOrigin: any[] = [];
  localListAirportDestination: any[] = [];
  listAirportOrigin: any[] = [];
  listAirportDestination: any[] = [];

  transitOptions = [
    {
      name: "Langsung",
      value: 0
    },
    {
      name: "1 transit",
      value: 1
    },
    {
      name: "≥ 2 transit",
      value: 2
    }
  ];

  arrivalAndDepartureOptions = [
    {
      name: "Kedatangan tercepat",
      value: "arrival_time"
    },
    {
      name: "Keberangkatan tercepat",
      value: "departure_time"
    },
    {
      name: "Leg terpendek",
      value: "shortest_lag"
    }
  ]

  get selectedFlightSortName() {
    return this.arrivalAndDepartureOptions.find(item => item.value === this.flightTime)?.name || 'Urutkan'
  }

  get transitFilterCounter() {
    return this.transitNumber.length > 0 ? `(${this.transitNumber.length})` : ''
  }

  get isTransitFilterFilled() {
    return this.transitNumber.length > 0
  }
  
  get headerMessage() {
    const message = ({
      loading: "Daftar jadwal pesawat sedang dimuat",
      internet: "Jaringan internet terputus",
      server: "Terjadi gangguan sistem",
      empty: "Jadwal keberangkatan kargo pesawat tidak tersedia"
    }) as any
    return this.isLoading ? message['loading'] : this.isEmpty? message['empty'] : message[this.error];
  }

  get bodyMessage() {
    const message = ({
      loading: "Kami sedang mencari jadwal yang sesuai untuk Anda.",
      internet: 'Periksa jaringan internal Anda lalu klik "Muat ulang".',
      server: 'Klik "Muat ulang" atau coba beberapa saat lagi.',
      empty: "Ubah & cek kembali pencarian jadwal pesawat Anda."
    }) as any
    return this.isLoading ? message['loading'] : this.isEmpty? message['empty'] : message[this.error];
  }

  get iconStatus() {
    const icon = ({
      internet: "error-internet",
      server: "error-token-ngen",
      empty: "empty-flight-schedule"
    }) as any
    return this.isEmpty ? icon['empty'] : icon[this.error];
  }

  get buttonTitle() {
    const title = ({
      internet: "Muat ulang",
      server: "Muat ulang",
      empty: "Ubah pencarian"
    }) as any
    return this.isEmpty ? title['empty'] : title[this.error];
  }

  get buttonIconLeft() {
    return !this.isEmpty && "refresh-white";
  }

  private compareByTime(a: SearchFlight, b: SearchFlight): number {
    return this.flightTime === "shortest_lag" 
      ? this.compareByTransit(a, b) 
      : this.flightTime === "departure_time"
      ? a.departureTime - b.departureTime
      : a.arrivalTime - b.arrivalTime;
  }

  private compareByOriginMatch(a: SearchFlight, b: SearchFlight): number {
    const aMatches = a.originAirportCode === CargoController.form.airportOrigin.airportCode;
    const bMatches = b.originAirportCode === CargoController.form.airportOrigin.airportCode;

    if (aMatches && !bMatches) return -1;
    if (!aMatches && bMatches) return 1;
    return 0;
  }

  private compareByDestinationMatch(a: SearchFlight, b: SearchFlight): number {
    const selectedDestination = CargoController.form.airportDestination.airportCode;

    const aMatches = a.destinationAirportCode === selectedDestination;
    const bMatches = b.destinationAirportCode === selectedDestination;

    if (aMatches && !bMatches) return -1;
    if (!aMatches && bMatches) return 1;
    return 0;
  }

  private compareByTransit(a: SearchFlight, b: SearchFlight): number {
    const transitComparison = a.transitNumber - b.transitNumber;
  
    // If transit numbers are different, return the transit comparison
    if (transitComparison !== 0) {
      return transitComparison;
    }
    
    // If transit numbers are the same, then compare by arrival time (earliest arrival first)
    return a.arrivalTime - b.arrivalTime;
  }

  get filteredFlightSchedules() {
    if (!this.data.length) return [];
    const unfilteredData = this.data;
    const newData = [...this.data].sort((a: SearchFlight, b: SearchFlight) => {
      const originComparison = this.compareByOriginMatch(a, b);
      const destinationComparison = this.compareByDestinationMatch(a, b);
      return originComparison || destinationComparison || this.compareByTime(a, b);
    });
    return this.flightTime || !this.isSortingEnabled ? newData : unfilteredData;
  }

  isFilterFlight(item: SearchFlight) {
    const originPass = this.listAirportOrigin.length 
      ? this.listAirportOrigin.includes(item.originAirportCode) 
      : true;
    
    const destinationPass = this.listAirportDestination.length 
      ? this.listAirportDestination.includes(item.destinationAirportCode) 
      : true;

    const transitPass = this.transitNumber.length
      ? this.transitNumber.some((t: number) =>
          t === 2 ? item.transitNumber >= 2 : item.transitNumber === t
        )
      : true;

    return originPass && destinationPass && transitPass;
  }

  get isEmpty() {
    return this.filteredFlightSchedules.length === 0 && this.error === "";
  }

  onActionButtonClicked() {
    if (this.isEmpty) {
      this.$emit("onChangeSearch");
    } else {
      this.$emit("onReload");
    }
  }

  get mapOriginAirport() {
    return Array.from(new Set(this.data.map((item: SearchFlight) => item.originAirportCode)));
  }

  get mapDestinationAirport() {
    return Array.from(new Set(this.data.map((item: SearchFlight) => item.destinationAirportCode)));
  }

  get isListAirportFilterFilled() {
    return this.listAirportOrigin.length || this.listAirportDestination.length > 0
  }

  get listAirportFilterCounter() {
    return this.listAirportOrigin.length || this.listAirportDestination.length ? `(${this.listAirportOrigin.length + this.listAirportDestination.length})` : ''
  }

  resetFlightTime() {
    this.flightTime = "";
    this.openFilter = false;
  }

  get isSortingEnabled() {
    return FlagsPermissionHydra.cargo_flight_priority_sorting_enabled.isEnabled();
  }
}
